/* eslint-disable react-refresh/only-export-components */
import { GitCompareArrows, TrendingDown, TrendingUp, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MetricCard } from '../primitives'

interface FunctionalUnit {
  quantity: number
  unit: string
  activity_name?: string
}

interface CategoryScore {
  score: number
  unit: string
}

interface ScenarioCategoryScore extends CategoryScore {
  delta_abs: number
  delta_pct: number | null
}

interface PinnedVersion {
  seq: number
  graph_hash: string
  created_at: string
}

interface ScenarioRow {
  name: string
  source?: string
  scores: Record<string, ScenarioCategoryScore>
  overrides_applied?: number
  pinned_version?: PinnedVersion
  stale?: boolean
  stale_reasons?: string[]
}

interface VersionGroup {
  version: {
    id?: string
    seq: number
    graph_hash: string
  }
  base: { scores: Record<string, CategoryScore> }
  scenarios: ScenarioRow[]
}

export interface ScenarioResultData {
  // Old shape (cached results)
  base?: { scores: Record<string, CategoryScore> }
  scenarios?: ScenarioRow[]
  // New shape (version-grouped results)
  version_groups?: VersionGroup[]
  functional_unit?: FunctionalUnit
  method_specification?: string
  framework?: string
  database?: string
  calculation_time?: number
}

// Type guard used by resolveRenderer / TaskStatusRenderer delegation
export function isScenarioResult(data: unknown): data is ScenarioResultData {
  if (typeof data !== 'object' || data === null) return false
  const obj = data as Record<string, unknown>
  // New shape: has version_groups
  if (Array.isArray(obj.version_groups)) {
    if (obj.version_groups.length > 0) {
      const vg = (obj as { version_groups?: unknown }).version_groups
      if (Array.isArray(vg) && vg.length > 0) {
        const first = vg[0] as { base?: { scores?: unknown }; scenarios?: unknown }
        return (
          typeof first?.base?.scores === 'object' && first.base.scores !== null &&
          Array.isArray(first?.scenarios)
        )
      }
    }
    return false
  }
  // Old shape: has base and scenarios
  return (
    typeof obj.base === 'object' &&
    obj.base !== null &&
    'scores' in (obj.base as object) &&
    Array.isArray(obj.scenarios)
  )
}

function formatScore(score: number): string {
  if (Math.abs(score) < 0.001 && score !== 0) {
    return score.toExponential(3)
  }
  return score.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })
}

function VersionLabel({ seq }: { seq: number }) {
  return (
    <span className="ml-2 inline-flex items-center text-[10px] font-medium text-muted-foreground">
      v{seq}
    </span>
  )
}

function StaleBadge({ scenario }: { scenario: ScenarioRow }) {
  if (!scenario.stale) {
    return null
  }
  const reasons = scenario.stale_reasons ?? []
  const tooltipText = `Built on v${scenario.pinned_version?.seq}; project has changed since (${reasons.join(', ')}). Rebase to update.`
  return (
    <span
      className="ml-2 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
      title={tooltipText}
    >
      pinned v{scenario.pinned_version?.seq} · stale
    </span>
  )
}

function DeltaBadge({ pct }: { pct: number | null }) {
  if (pct === null) {
    return <span className="text-[10px] text-muted-foreground">n/a</span>
  }
  const isFlat = Math.abs(pct) < 0.005
  // Lower impact = good (green); higher = bad (red)
  const Icon = isFlat ? Minus : pct < 0 ? TrendingDown : TrendingUp
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-[10px] font-medium tabular-nums',
        isFlat ? 'text-muted-foreground' : pct < 0 ? 'text-success' : 'text-destructive',
      )}
    >
      <Icon className="w-3 h-3" />
      {pct > 0 ? '+' : ''}
      {pct.toFixed(1)}%
    </span>
  )
}

function ScenarioTable({
  scenarios,
  baseScores,
  categories,
}: {
  scenarios: ScenarioRow[]
  baseScores: Record<string, CategoryScore>
  categories: string[]
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="bg-muted/50 border-b border-border">
            <th className="px-3 py-1.5 font-semibold text-muted-foreground whitespace-nowrap">
              Impact Category
            </th>
            <th className="px-3 py-1.5 font-semibold text-muted-foreground whitespace-nowrap">
              Base
            </th>
            {scenarios.map((s) => (
              <th
                key={s.name}
                className="px-3 py-1.5 font-semibold text-muted-foreground whitespace-nowrap"
                title={
                  s.overrides_applied !== undefined
                    ? `${s.overrides_applied} override${s.overrides_applied !== 1 ? 's' : ''} applied`
                    : undefined
                }
              >
                <div className="flex items-center">
                  <span>{s.name}</span>
                  {s.stale ? (
                    <StaleBadge scenario={s} />
                  ) : (
                    s.pinned_version && <VersionLabel seq={s.pinned_version.seq} />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {categories.map((category, rowIndex) => {
            const base = baseScores[category]
            return (
              <tr
                key={category}
                className={cn(
                  'border-b border-border/50 last:border-0',
                  rowIndex % 2 === 0 ? 'bg-transparent' : 'bg-muted/20',
                )}
              >
                <td className="px-3 py-1.5 max-w-[220px]">
                  <div className="truncate" title={category}>
                    {category}
                  </div>
                  <div className="text-[10px] text-muted-foreground">{base.unit}</div>
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">
                  {formatScore(base.score)}
                </td>
                {scenarios.map((s) => {
                  const entry = s.scores[category]
                  if (!entry) {
                    return (
                      <td key={s.name} className="px-3 py-1.5 text-muted-foreground">
                        -
                      </td>
                    )
                  }
                  return (
                    <td key={s.name} className="px-3 py-1.5 whitespace-nowrap tabular-nums">
                      <div>{formatScore(entry.score)}</div>
                      <DeltaBadge pct={entry.delta_pct} />
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function ScenarioResultRenderer({ data }: { data: ScenarioResultData }) {
  // Support both old (base/scenarios) and new (version_groups) shapes
  const isNewShape = data.version_groups && data.version_groups.length > 0
  const hasOldShape = data.base && data.base.scores && Object.keys(data.base.scores).length > 0

  if (!isNewShape && !hasOldShape) {
    return null
  }

  return (
    <div className="space-y-3">
      {/* Header card */}
      <div className="rounded-lg border p-3 bg-success-muted/30 border-success/30">
        <div className="flex items-center gap-2">
          <GitCompareArrows className="w-5 h-5 text-success" />
          <div className="flex-1">
            <div className="font-medium text-sm">Scenario Analysis</div>
            <p className="text-xs text-muted-foreground">
              {isNewShape ? (
                <>
                  {data.version_groups!.length} version{data.version_groups!.length !== 1 ? 's' : ''}
                  {(() => {
                    const totalScenarios = data.version_groups!.reduce(
                      (sum, g) => sum + g.scenarios.length, 0
                    )
                    return totalScenarios > 0 && (
                      <> with {totalScenarios} scenario{totalScenarios !== 1 ? 's' : ''}</>
                    )
                  })()}
                </>
              ) : (
                <>
                  Base + {data.scenarios!.length} scenario{data.scenarios!.length !== 1 ? 's' : ''}
                </>
              )}
              {data.functional_unit && (
                <>
                  {' '}
                  — per {data.functional_unit.quantity} {data.functional_unit.unit}
                  {data.functional_unit.activity_name && (
                    <> of {data.functional_unit.activity_name}</>
                  )}
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Render version groups (new shape) */}
      {isNewShape && data.version_groups && (
        <div className="space-y-3">
          {data.version_groups.map((group, groupIndex) => {
            const categories = Object.keys(group.base.scores)
            if (categories.length === 0) return null
            return (
              <div key={groupIndex} className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground">
                  Base version v{group.version.seq}
                </div>
                <ScenarioTable
                  scenarios={group.scenarios}
                  baseScores={group.base.scores}
                  categories={categories}
                />
              </div>
            )
          })}
        </div>
      )}

      {/* Render old shape (backward compatibility with cached results) */}
      {!isNewShape && hasOldShape && (
        <>
          {(() => {
            const categories = Object.keys(data.base!.scores)
            if (categories.length === 0) return null
            return (
              <ScenarioTable
                scenarios={data.scenarios || []}
                baseScores={data.base!.scores}
                categories={categories}
              />
            )
          })()}
        </>
      )}

      {/* Context chips */}
      <div className="flex flex-wrap gap-2">
        {data.framework && <MetricCard label="Framework" value={data.framework} size="sm" />}
        {!data.framework && data.method_specification && (
          <MetricCard
            label="Method"
            value={data.method_specification.split(' > ')[1] || data.method_specification}
            size="sm"
          />
        )}
        {data.database && (
          <MetricCard
            label="Database"
            value={data.database.replace('ecoinvent-', '').replace('-cutoff', '')}
            size="sm"
          />
        )}
        {data.calculation_time !== undefined && (
          <MetricCard label="Calc Time" value={data.calculation_time.toFixed(2)} unit="s" size="sm" />
        )}
      </div>
    </div>
  )
}
